<template>
  <div class="planner bg-app vh-100">
    <v-container fluid class="px-0">
      <v-row v-if="!loading">
        <v-col class="px-2">
          <v-card flat class="transparent">
            <div class="px-4 mx-1 mb-6">
              <div class="mb-2">
                <v-btn small @click="$router.go(-1)"> <v-icon>mdi-chevron-left</v-icon> back </v-btn>
              </div>

              <div class="mb-1">
                <v-chip :color="statusColor(currentStatusCode)" x-small text-color="white" class="text-uppercase">
                  {{ currentStatusText }}
                </v-chip>
              </div>
              <div class="overline">
                {{
                  plan?.type === "report"
                    ? this.$t("t_planner_type_report")
                    : plan?.type === "task"
                    ? this.$t("t_planner_type_task")
                    : plan?.type === "inspection"
                    ? this.$t("t_planner_type_inspection")
                    : this.$t("t_planners")
                }}
                {{ submission.completedOn ? " • " + formatDateTime(submission.completedOn) : "" }}
              </div>
              <h2 class="mb-3">{{ planner.name ? planner.name : "Planner" }}</h2>
              <div class="subtitle-1" v-if="planner.description">{{ planner.description }}</div>
              <div class="ma-0 pa-0" v-if="dueDate?.dueDateTime">
                <div class="d-flex align-center">
                  <span class="text-subtitle-2 mr-2">{{ $t("t_due") }}:</span>
                  <span> {{ formatDateTime(dueDate.dueDateTime) }} </span>
                </div>
              </div>
              <v-divider class="my-4"></v-divider>
              <div v-for="(task, taskIndex) in tasks" :key="'stepItem-' + taskIndex" class="mb-8">
                <div>
                  <div class="d-flex align-center justify-space-between">
                    <div class="overline" style="line-height: 12px">
                      {{
                        plan?.type === "report"
                          ? $t("t_inspection_item_num", { num: taskIndex + 1 })
                          : $t("t_task_num", { num: taskIndex + 1 })
                      }}
                    </div>
                    <div v-if="plan?.type === 'inspection'">
                      <!-- Inspection planners: hidden, kept for compatibility -->
                    </div>
                    <div v-else-if="(plan?.type === 'safety' || plan?.type === 'report') && taskHasRiskControls(task)">
                      <!-- Safety/Report planners with risk controls: Show completed status based on all controls verified -->
                      <div v-if="areAllTaskControlsVerified(task)" class="d-flex align-center">
                        <v-icon color="success" small class="mr-1">mdi-check-circle</v-icon>
                        <span class="text-caption success--text font-weight-medium">{{ $t("t_completed") }}</span>
                        <span v-if="task.completedOn" class="text-caption grey--text ml-2"
                          >&bull; {{ formatDateTimeContextual(task.completedOn) }}</span
                        >
                      </div>
                      <div v-else class="d-flex align-center">
                        <span class="text-caption warning--text font-weight-medium">{{ $t("t_incomplete") }}</span>
                      </div>
                    </div>
                    <div v-else class="d-flex align-center">
                      <!-- Tasks without risk controls or other planner types: Show checkbox -->
                      <v-checkbox
                        v-model="task.status"
                        :disabled="isCompleted"
                        :true-value="'done'"
                        :false-value="'Incomplete'"
                        dense
                        hide-details
                        class="mt-0 pt-0"
                        @change="(val) => updateTaskStatus(task, val)"
                      >
                        <template v-slot:label>
                          <span class="text-caption">{{ $t("t_completed") }}</span>
                        </template>
                      </v-checkbox>
                      <span v-if="task.completedOn" class="text-caption grey--text ml-2"
                        >&bull; {{ formatDateTimeContextual(task.completedOn) }}</span
                      >
                    </div>
                  </div>
                  <h3 :class="{ 'text-decoration-line-through': task.completed }" class="pb-2">{{ task.name }}</h3>
                </div>
                <div class="pb-2">{{ task.description }}</div>
                <div>
                  <span class="overline">{{ $t("t_expected_duration") }}:</span> {{ task.duration }}
                </div>
                <div></div>
                <div v-if="task.prerequisites?.length">
                  <label class="overline">{{ $t("t_prerequisites") }}</label>
                  <div class="mb-3">
                    <ul>
                      <li v-for="(p, idx) in task.prerequisites" :key="'p-' + idx">
                        {{ p }}
                      </li>
                    </ul>
                  </div>
                </div>
                <div v-if="task.resources && task.resources.length && task.resources[0].name">
                  <label class="overline">{{ $t("t_recommended_resources") }}</label>
                  <ul>
                    <li v-for="(r, idx) in task.resources" :key="'r-' + idx">{{ r.name }}</li>
                  </ul>
                </div>
                <div class="pt-3">
                  <div v-if="plan?.type === 'inspection' && task.inspectionCriteria?.length">
                    <div
                      v-for="(criteria, criteriaIndex) in task.inspectionCriteria"
                      :key="'criteria-' + taskIndex + '-' + criteriaIndex"
                      class="mb-6"
                    >
                      <div class="inspection-criteria-item">
                        <h4 class="text-h6 font-weight-bold mb-2">{{ criteria.name }}</h4>
                        <div class="d-flex align-center mb-3">
                          <v-chip
                            :color="getPriorityColor(criteria.priority)"
                            x-small
                            text-color="white"
                            class="text-uppercase"
                          >
                            {{ criteria.priority }}
                          </v-chip>
                          <v-spacer></v-spacer>
                          <span v-if="criteria.verifiedOn" class="caption grey--text"
                            >{{ $t("t_verified") }} {{ formatDateTimeContextual(criteria.verifiedOn) }}</span
                          >
                        </div>
                        <div v-if="criteria.regulatoryCodes?.length" class="mb-3">
                          <div class="d-flex align-center mb-2">
                            <v-icon small color="primary" class="mr-2">mdi-lightning-bolt</v-icon>
                            <span class="overline font-weight-bold">{{ $t("t_regulatory_codes") }}</span>
                          </div>
                          <div
                            v-for="(regCode, regIndex) in criteria.regulatoryCodes"
                            :key="'reg-' + criteriaIndex + '-' + regIndex"
                            class="regulatory-code-item mb-2 pa-2 pl-4 ml-1"
                            style="background: rgba(0, 0, 0, 0.23); border-radius: 4px"
                          >
                            <div class="d-flex align-start">
                              <div class="flex-grow-1">
                                <div class="font-weight-medium">
                                  {{ regCode.authority }} {{ regCode.code }}: {{ regCode.title }}
                                </div>
                                <div class="caption grey--text">{{ regCode.description }}</div>
                                <div v-if="regCode.regulationText" class="mt-1">
                                  <template v-if="shouldTruncateRegCode(regCode.regulationText)">
                                    <div v-if="!isRegCodeExpanded(`insp-reg-${criteriaIndex}-${regIndex}`)">
                                      {{ getTruncatedText(regCode.regulationText, 80) }}
                                    </div>
                                    <ul v-else class="multi-line-list">
                                      <li
                                        v-for="(line, index) in getMultiLineList(regCode.regulationText)"
                                        :key="'regulation-' + index"
                                      >
                                        {{ line }}
                                      </li>
                                    </ul>
                                    <v-btn
                                      x-small
                                      text
                                      color="primary"
                                      class="pa-0 mt-1"
                                      style="text-transform: none; min-width: auto"
                                      @click="toggleRegCodeExpansion(`insp-reg-${criteriaIndex}-${regIndex}`)"
                                    >
                                      {{
                                        isRegCodeExpanded(`insp-reg-${criteriaIndex}-${regIndex}`)
                                          ? $t("t_view_less")
                                          : $t("t_view_all")
                                      }}
                                      <v-icon x-small class="ml-1">
                                        {{
                                          isRegCodeExpanded(`insp-reg-${criteriaIndex}-${regIndex}`)
                                            ? "mdi-chevron-up"
                                            : "mdi-chevron-down"
                                        }}
                                      </v-icon>
                                    </v-btn>
                                  </template>
                                  <ul v-else class="multi-line-list">
                                    <li
                                      v-for="(line, index) in getMultiLineList(regCode.regulationText)"
                                      :key="'regulation-' + index"
                                    >
                                      {{ line }}
                                    </li>
                                  </ul>
                                </div>
                                <div v-if="regCode.purpose" class="mt-1">
                                  <div class="overline">{{ $t("t_purpose") }}</div>
                                  <template v-if="shouldTruncateRegCode(regCode.purpose)">
                                    <div v-if="!isRegCodeExpanded(`insp-purpose-${criteriaIndex}-${regIndex}`)">
                                      {{ getTruncatedText(regCode.purpose, 80) }}
                                    </div>
                                    <ul v-else class="multi-line-list">
                                      <li
                                        v-for="(line, index) in getMultiLineList(regCode.purpose)"
                                        :key="'purpose-' + index"
                                      >
                                        {{ line }}
                                      </li>
                                    </ul>
                                    <v-btn
                                      x-small
                                      text
                                      color="primary"
                                      class="pa-0 mt-1"
                                      style="text-transform: none; min-width: auto"
                                      @click="toggleRegCodeExpansion(`insp-purpose-${criteriaIndex}-${regIndex}`)"
                                    >
                                      {{
                                        isRegCodeExpanded(`insp-purpose-${criteriaIndex}-${regIndex}`)
                                          ? $t("t_view_less")
                                          : $t("t_view_all")
                                      }}
                                      <v-icon x-small class="ml-1">
                                        {{
                                          isRegCodeExpanded(`insp-purpose-${criteriaIndex}-${regIndex}`)
                                            ? "mdi-chevron-up"
                                            : "mdi-chevron-down"
                                        }}
                                      </v-icon>
                                    </v-btn>
                                  </template>
                                  <ul v-else class="multi-line-list">
                                    <li
                                      v-for="(line, index) in getMultiLineList(regCode.purpose)"
                                      :key="'purpose-' + index"
                                    >
                                      {{ line }}
                                    </li>
                                  </ul>
                                </div>
                                <div v-if="regCode.complianceAction" class="mt-1">
                                  <div class="overline">{{ $t("t_compliance_action") }}</div>
                                  <template v-if="shouldTruncateRegCode(regCode.complianceAction)">
                                    <div v-if="!isRegCodeExpanded(`insp-compliance-${criteriaIndex}-${regIndex}`)">
                                      {{ getTruncatedText(regCode.complianceAction, 80) }}
                                    </div>
                                    <ul v-else class="multi-line-list">
                                      <li
                                        v-for="(line, index) in getMultiLineList(regCode.complianceAction)"
                                        :key="'compliance-' + index"
                                      >
                                        {{ line }}
                                      </li>
                                    </ul>
                                    <v-btn
                                      x-small
                                      text
                                      color="primary"
                                      class="pa-0 mt-1"
                                      style="text-transform: none; min-width: auto"
                                      @click="toggleRegCodeExpansion(`insp-compliance-${criteriaIndex}-${regIndex}`)"
                                    >
                                      {{
                                        isRegCodeExpanded(`insp-compliance-${criteriaIndex}-${regIndex}`)
                                          ? $t("t_view_less")
                                          : $t("t_view_all")
                                      }}
                                      <v-icon x-small class="ml-1">
                                        {{
                                          isRegCodeExpanded(`insp-compliance-${criteriaIndex}-${regIndex}`)
                                            ? "mdi-chevron-up"
                                            : "mdi-chevron-down"
                                        }}
                                      </v-icon>
                                    </v-btn>
                                  </template>
                                  <ul v-else class="multi-line-list">
                                    <li
                                      v-for="(line, index) in getMultiLineList(regCode.complianceAction)"
                                      :key="'compliance-' + index"
                                    >
                                      {{ line }}
                                    </li>
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div v-if="criteria.acceptanceCriteria" class="mb-3">
                          <div class="d-flex align-center mb-2">
                            <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                            <span class="overline font-weight-bold">{{ $t("t_acceptance_criteria") }}</span>
                          </div>
                          <ul class="multi-line-list">
                            <li
                              v-for="(line, index) in getMultiLineList(criteria.acceptanceCriteria)"
                              :key="'acceptance-' + index"
                            >
                              {{ line }}
                            </li>
                          </ul>
                        </div>

                        <!-- Test Method -->
                        <div v-if="criteria.testMethod" class="mb-3">
                          <div class="d-flex align-center mb-2">
                            <v-icon small color="info" class="mr-2">mdi-waveform</v-icon>
                            <span class="overline font-weight-bold">{{ $t("t_test_measurement_method") }}</span>
                          </div>
                          <ul class="multi-line-list">
                            <li v-for="(line, index) in getMultiLineList(criteria.testMethod)" :key="'test-' + index">
                              {{ line }}
                            </li>
                          </ul>
                        </div>

                        <!-- Tolerance Values -->
                        <div v-if="criteria.toleranceValues" class="mb-3">
                          <div class="d-flex align-center mb-2">
                            <v-icon small color="warning" class="mr-2">mdi-ruler</v-icon>
                            <span class="overline font-weight-bold">{{ $t("t_tolerance_threshold_values") }}</span>
                          </div>
                          <ul class="multi-line-list">
                            <li
                              v-for="(line, index) in getMultiLineList(criteria.toleranceValues)"
                              :key="'tolerance-' + index"
                            >
                              {{ line }}
                            </li>
                          </ul>
                        </div>

                        <!-- Documentation Requirements -->
                        <div v-if="criteria.documentation" class="mb-3">
                          <div class="d-flex align-center mb-2">
                            <v-icon small color="primary" class="mr-2">mdi-file-document</v-icon>
                            <span class="overline font-weight-bold">{{ $t("t_documentation_requirements") }}</span>
                          </div>
                          <ul class="multi-line-list">
                            <li v-for="(line, index) in getMultiLineList(criteria.documentation)" :key="'doc-' + index">
                              {{ line }}
                            </li>
                          </ul>
                        </div>

                        <!-- Corrective Action -->
                        <div v-if="criteria.correctiveAction" class="mb-3">
                          <div class="d-flex align-center mb-2">
                            <v-icon small color="error" class="mr-2">mdi-alert</v-icon>
                            <span class="overline font-weight-bold">{{ $t("t_corrective_action") }}</span>
                          </div>
                          <ul class="multi-line-list">
                            <li
                              v-for="(line, index) in getMultiLineList(criteria.correctiveAction)"
                              :key="'corrective-' + index"
                            >
                              {{ line }}
                            </li>
                          </ul>
                        </div>

                        <!-- Pass Inspection Checkbox - Above Evidence Media -->
                        <div
                          class="mb-4 pass-inspection-container"
                          :class="{
                            'is-verified': criteria.status === 'verified',
                            'is-disabled': isCompleted,
                          }"
                          @click="!isCompleted && toggleCriteriaStatus(task, criteria, $event)"
                          :style="{ cursor: isCompleted ? 'default' : 'pointer' }"
                        >
                          <div class="d-flex align-center mb-2">
                            <v-checkbox
                              v-model="criteria.status"
                              :disabled="isCompleted"
                              :true-value="'verified'"
                              :false-value="'pending'"
                              dense
                              hide-details
                              @change="(val) => updateCriteriaStatus(task, criteria, val)"
                            >
                              <template v-slot:label>
                                <span class="text-body-1 font-weight-medium d-flex align-center">
                                  {{ $t("t_meets_all_acceptance_requirements") }}
                                </span>
                              </template>
                            </v-checkbox>
                          </div>
                        </div>

                        <!-- Evidence Media -->
                        <div class="mb-4">
                          <div class="d-flex align-center mb-2">
                            <v-icon small color="primary" class="mr-2">mdi-camera</v-icon>
                            <span class="body-2">{{ $t("t_verification_evidence") }}</span>
                          </div>
                          <v-btn
                            small
                            color="primary"
                            @click="addCriteriaMedia(task, criteria)"
                            :disabled="isCompleted"
                            class="mb-2"
                          >
                            <v-icon left small>mdi-camera</v-icon>
                            {{ $t("t_add_evidence_media") }}
                          </v-btn>

                          <div v-if="criteria.verificationMedia?.length" class="verification-media-grid">
                            <div
                              v-for="media in criteria.verificationMedia"
                              :key="media.id"
                              class="verification-media-item"
                            >
                              <v-img
                                :src="media.imageSource"
                                aspect-ratio="1"
                                class="grey lighten-2"
                                @click="previewImage(media.imageSource)"
                              >
                                <template v-slot:placeholder>
                                  <v-row class="fill-height ma-0" align="center" justify="center">
                                    <v-progress-circular indeterminate></v-progress-circular>
                                  </v-row>
                                </template>
                              </v-img>
                            </div>
                          </div>
                        </div>

                        <!-- Comments -->
                        <div class="pt-3">
                          <v-textarea
                            v-model="criteria.comments"
                            :label="$t('t_verification_comments')"
                            rows="2"
                            outlined
                            filled
                            auto-grow
                            dense
                            hide-details
                            :disabled="isCompleted"
                            @input="triggerAutoSave()"
                          ></v-textarea>
                        </div>
                      </div>
                      <v-divider class="my-4" v-if="criteriaIndex < task.inspectionCriteria.length - 1"></v-divider>
                    </div>
                  </div>

                  <!-- Hazards (for safety/report type planners) -->
                  <div v-for="(hazard, hazardIndex) in task.hazards" :key="'hazardItem-' + hazardIndex">
                    <div v-for="(type, subTypeIndex) in hazard.subTypes" :key="'typedItem-' + subTypeIndex">
                      <div v-if="hazard.type && type.name">
                        <span class="text-capitalize">
                          <label class="overline" style="line-height: 12px">
                            <v-icon color="orange darken-2" small>mdi-alert-box</v-icon
                            ><span class="pl-2" style="position: relative; top: 1px">Risk {{ subTypeIndex + 1 }}</span>
                          </label>
                          <h4>{{ type.name }} : {{ hazard.type }}</h4>
                        </span>
                        <div class="ml-6 mt-2 safety-controls-container">
                          <v-row class="my-2">
                            <v-col cols="auto" class="text-center" style="margin-top: -5px">
                              <v-progress-circular
                                :value="getRiskRating(type.likelihood, type.impact)"
                                :size="90"
                                :width="10"
                                color="yellow darken-2"
                              >
                                <v-row justify="center" class="fill-height">
                                  <v-col class="text-center">
                                    <strong>{{ Math.ceil(getRiskRating(type.likelihood, type.impact)) }}%</strong>
                                    <div class="caption risk-rating">Risk Rating</div>
                                  </v-col>
                                </v-row>
                              </v-progress-circular>
                            </v-col>

                            <v-col class="d-flex flex-column justify-center">
                              <v-progress-linear
                                rounded
                                striped
                                class="mb-4"
                                :color="setTrackColor(type.likelihood) + ' darken-4'"
                                :value="type.likelihood != 0 ? Math.ceil((type.likelihood / 5) * 100) : 5"
                                height="15"
                              >
                                <span class="overline">Likelihood</span>
                              </v-progress-linear>

                              <v-progress-linear
                                rounded
                                striped
                                class="mb-4"
                                :color="setTrackColor(type.impact) + ' darken-4'"
                                :value="Math.ceil((type.impact / 5) * 100)"
                                height="15"
                              >
                                <span class="overline">Impact</span>
                              </v-progress-linear>

                              <v-progress-linear
                                rounded
                                striped
                                class="mb-4"
                                :color="setTrackColor(type.severity) + ' darken-4'"
                                :value="type.severity != 0 ? Math.ceil((type.severity / 5) * 100) : 5"
                                height="15"
                              >
                                <span class="overline">Severity</span>
                              </v-progress-linear>
                            </v-col>
                          </v-row>
                          <!-- Source Evidence section -->
                          <div v-if="hazard.sourceEvidence?.images?.length" class="mt-3 mb-4">
                            <label class="overline mb-2">Source Evidence</label>
                            <div
                              v-for="(evidence, evidenceIndex) in hazard.sourceEvidence.images"
                              :key="'evidence-' + evidenceIndex"
                              class="evidence-item mb-3"
                            >
                              <div class="evidence-container">
                                <!-- Image -->
                                <div class="image-section pa-3">
                                  <div class="image-container clickable" @click="previewImage(evidence.imageSource)">
                                    <img
                                      v-if="evidence.imageSource"
                                      :src="evidence.imageSource"
                                      class="evidence-image"
                                    />
                                    <div v-else class="loading-placeholder">
                                      <v-progress-circular indeterminate></v-progress-circular>
                                    </div>
                                    <div class="image-overlay">
                                      <v-icon color="white">mdi-magnify</v-icon>
                                    </div>
                                  </div>
                                </div>

                                <!-- Details Section -->
                                <div class="evidence-details flex-grow-1 pa-3">
                                  <!-- Observations -->
                                  <div v-if="evidence.observations?.length" class="mb-3">
                                    <div class="d-flex align-center mb-1">
                                      <v-icon x-small color="primary" class="mr-2">mdi-eye</v-icon>
                                      <span class="caption font-weight-medium">Observations</span>
                                    </div>
                                    <ul class="mt-1 mb-0">
                                      <template v-if="typeof evidence.observations === 'string'">
                                        <li class="caption">{{ evidence.observations }}</li>
                                      </template>
                                      <template v-else>
                                        <li
                                          v-for="(obs, idx) in evidence.observations"
                                          :key="`obs-${taskIndex}-${hazardIndex}-${evidenceIndex}-${idx}`"
                                          class="caption"
                                        >
                                          {{ obs }}
                                        </li>
                                      </template>
                                    </ul>
                                  </div>

                                  <!-- Sentiment -->
                                  <div v-if="evidence.sentiment" class="mb-2">
                                    <div class="d-flex align-center mb-1">
                                      <v-icon x-small color="primary" class="mr-2">mdi-emoticon</v-icon>
                                      <span class="caption font-weight-medium">Sentiment</span>
                                    </div>
                                    <div class="d-flex align-center">
                                      <v-chip
                                        small
                                        :color="getSentimentColor(evidence.sentiment)"
                                        class="px-2 pl-4"
                                        :class="getSentimentColor(evidence.sentiment) === `error` ? 'white--text' : ''"
                                      >
                                        <v-icon x-small left>{{ getSentimentIcon(evidence.sentiment) }}</v-icon>
                                        <span class="ml-1">{{ getSentimentText(evidence.sentiment) }}</span>
                                      </v-chip>
                                    </div>
                                  </div>

                                  <!-- Comments -->
                                  <div
                                    v-if="evidence.comments?.length && evidence.comments[0]?.text"
                                    class="mt-4 comments-section"
                                  >
                                    <div class="d-flex align-center text-caption">
                                      <v-icon x-small color="primary" class="mr-2">mdi-comment-multiple</v-icon>
                                      {{ evidence.comments.length }} Comments
                                    </div>
                                    <v-list dense class="comment-list pa-0 mt-0">
                                      <v-list-item
                                        v-for="comment in evidence.comments"
                                        :key="comment.createdAt"
                                        class="pa-0"
                                      >
                                        <v-list-item-content>
                                          <v-list-item-subtitle class="text-caption">
                                            <span class="mr-2">
                                              {{ formatDateTime(comment.createdAt) }}
                                            </span>
                                            <v-chip
                                              v-if="comment.severity"
                                              x-small
                                              label
                                              class="ml-1"
                                              :color="getSeverityColor(comment.severity)"
                                            >
                                              Severity {{ comment.severity }}
                                            </v-chip>
                                          </v-list-item-subtitle>
                                          <v-list-item-title class="text-body-2 mt-1">
                                            {{ comment.text }}
                                          </v-list-item-title>
                                        </v-list-item-content>
                                      </v-list-item>
                                    </v-list>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <v-divider
                            class="my-4"
                            v-if="hazard.regulatoryCodes?.length && hazard.regulatoryCodes[0].code"
                          ></v-divider>
                          <!-- Regulatory Codes Section -->
                          <template v-if="hazard.regulatoryCodes?.length && hazard.regulatoryCodes[0].code">
                            <label class="overline mr-2">{{ $t("t_regulatory_codes") }}</label>
                            <div v-for="(code, codeIndex) in hazard.regulatoryCodes" :key="'codedItem-' + code.code">
                              <div class="pa-2 pt-0 px-0 mb-2" rounded v-if="code">
                                <div class="pl-3">
                                  <div class="font-weight-medium">{{ code.title }}</div>
                                  <div class="d-flex align-center my-1">
                                    <v-chip small :color="getStatusColor(code.status)" label outlined>
                                      {{ code.authority }}
                                      {{ code.code }}
                                    </v-chip>
                                  </div>
                                  <div class="text-caption">
                                    {{ code.description }}
                                  </div>
                                  <div v-if="code.complianceAction" class="mt-1">
                                    <template v-if="shouldTruncateRegCode(code.complianceAction)">
                                      <div
                                        v-if="!isRegCodeExpanded(`haz-comp-${taskIndex}-${hazardIndex}-${codeIndex}`)"
                                      >
                                        <v-icon x-small color="primary" class="mr-2">mdi-arrow-right-circle</v-icon>
                                        <span class="text-caption">{{
                                          getTruncatedText(code.complianceAction, 80)
                                        }}</span>
                                      </div>
                                      <div v-else class="d-flex align-start">
                                        <v-icon x-small color="primary" class="mr-2 mt-1"
                                          >mdi-arrow-right-circle</v-icon
                                        >
                                        <span class="text-caption">{{ code.complianceAction }}</span>
                                      </div>
                                      <v-btn
                                        x-small
                                        text
                                        color="primary"
                                        class="pa-0 mt-1 ml-5"
                                        style="text-transform: none; min-width: auto"
                                        @click="
                                          toggleRegCodeExpansion(`haz-comp-${taskIndex}-${hazardIndex}-${codeIndex}`)
                                        "
                                      >
                                        {{
                                          isRegCodeExpanded(`haz-comp-${taskIndex}-${hazardIndex}-${codeIndex}`)
                                            ? $t("t_view_less")
                                            : $t("t_view_all")
                                        }}
                                        <v-icon x-small class="ml-1">
                                          {{
                                            isRegCodeExpanded(`haz-comp-${taskIndex}-${hazardIndex}-${codeIndex}`)
                                              ? "mdi-chevron-up"
                                              : "mdi-chevron-down"
                                          }}
                                        </v-icon>
                                      </v-btn>
                                    </template>
                                    <div v-else class="d-flex align-center">
                                      <v-icon x-small color="primary" class="mr-2">mdi-arrow-right-circle</v-icon>
                                      <span class="text-caption">{{ code.complianceAction }}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </template>
                          <v-divider class="my-4" v-if="plan?.type === 'report'"></v-divider>
                          <h3>{{ $t("t_safety_controls_verification") }}</h3>

                          <!-- 1. Control Measures -->
                          <template v-if="splitCommaString(type.controlMeasures).length">
                            <label class="overline">{{ $t("t_risk_controls") }}</label>
                            <v-simple-table dense class="mb-4 compact-table">
                              <template v-slot:default>
                                <thead>
                                  <tr>
                                    <th class="text-left" style="width: 100%">{{ $t("t_risk_controls") }}</th>
                                    <th class="text-center">{{ $t("t_status") }}</th>
                                    <th class="text-center">{{ $t("t_verified") }}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr v-for="control in getControlItems(type, 'ctrl')" :key="control.id">
                                    <td>{{ control.label }}</td>
                                    <td class="status-cell">
                                      <v-checkbox
                                        :input-value="type.verifications?.[control.id] ? true : false"
                                        dense
                                        hide-details
                                        :disabled="isCompleted"
                                        @change="(checked) => updateVerification(type, control.id, checked)"
                                      ></v-checkbox>
                                    </td>
                                    <td class="caption date-cell">
                                      {{
                                        type.verifications?.[control.id]
                                          ? formatDateTimeContextual(type.verifications[control.id])
                                          : "-"
                                      }}
                                    </td>
                                  </tr>
                                </tbody>
                              </template>
                            </v-simple-table>
                          </template>

                          <!-- 2. Risk Mitigation Strategies -->
                          <template v-if="splitCommaString(type.mitigationStrategies).length">
                            <label class="overline">{{
                              plan?.type === "report"
                                ? $t("t_actions_to_reduce_risk")
                                : $t("t_strategies_to_reduce_risk")
                            }}</label>
                            <v-simple-table dense class="mb-4 compact-table">
                              <template v-slot:default>
                                <thead>
                                  <tr>
                                    <th class="text-left" style="width: 100%">
                                      {{ plan?.type === "report" ? $t("t_actions") : $t("t_strategies") }}
                                    </th>
                                    <th class="text-center">{{ $t("t_status") }}</th>
                                    <th class="text-center">{{ $t("t_verified") }}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr v-for="control in getControlItems(type, 'mit')" :key="control.id">
                                    <td>{{ control.label }}</td>
                                    <td class="status-cell">
                                      <v-checkbox
                                        :input-value="type.verifications?.[control.id] ? true : false"
                                        dense
                                        hide-details
                                        :disabled="isCompleted"
                                        @change="(checked) => updateVerification(type, control.id, checked)"
                                      ></v-checkbox>
                                    </td>
                                    <td class="caption date-cell">
                                      {{
                                        type.verifications?.[control.id]
                                          ? formatDateTimeContextual(type.verifications[control.id])
                                          : "-"
                                      }}
                                    </td>
                                  </tr>
                                </tbody>
                              </template>
                            </v-simple-table>
                          </template>

                          <!-- 3. Training Requirements -->
                          <template v-if="splitCommaString(type.requiredTraining).length">
                            <label class="overline">{{ $t("t_training_required") }}</label>
                            <v-simple-table dense class="mb-4 compact-table">
                              <template v-slot:default>
                                <thead>
                                  <tr>
                                    <th class="text-left" style="width: 100%">{{ $t("t_required_training") }}</th>
                                    <th class="text-center">{{ $t("t_status") }}</th>
                                    <th class="text-center">{{ $t("t_verified") }}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr v-for="control in getControlItems(type, 'train')" :key="control.id">
                                    <td>{{ control.label }}</td>
                                    <td class="status-cell">
                                      <v-checkbox
                                        :input-value="type.verifications?.[control.id] ? true : false"
                                        dense
                                        hide-details
                                        :disabled="isCompleted"
                                        @change="(checked) => updateVerification(type, control.id, checked)"
                                      ></v-checkbox>
                                    </td>
                                    <td class="caption date-cell">
                                      {{
                                        type.verifications?.[control.id]
                                          ? formatDateTimeContextual(type.verifications[control.id])
                                          : "-"
                                      }}
                                    </td>
                                  </tr>
                                </tbody>
                              </template>
                            </v-simple-table>
                          </template>

                          <!-- 4. PPE Requirements -->
                          <template v-if="splitCommaString(type.ppe).length">
                            <label class="overline">{{ $t("t_ppe_title") }}</label>
                            <v-simple-table dense class="mb-4 compact-table">
                              <template v-slot:default>
                                <thead>
                                  <tr>
                                    <th class="text-left" style="width: 100%">{{ $t("t_ppe_requirements") }}</th>
                                    <th class="text-center">{{ $t("t_status") }}</th>
                                    <th class="text-center">{{ $t("t_verified") }}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr v-for="control in getControlItems(type, 'ppe')" :key="control.id">
                                    <td>{{ control.label }}</td>
                                    <td class="status-cell">
                                      <v-checkbox
                                        :input-value="type.verifications?.[control.id] ? true : false"
                                        dense
                                        hide-details
                                        :disabled="isCompleted"
                                        @change="(checked) => updateVerification(type, control.id, checked)"
                                      ></v-checkbox>
                                    </td>
                                    <td class="caption date-cell">
                                      {{
                                        type.verifications?.[control.id]
                                          ? formatDateTimeContextual(type.verifications[control.id])
                                          : "-"
                                      }}
                                    </td>
                                  </tr>
                                </tbody>
                              </template>
                            </v-simple-table>
                          </template>

                          <div class="mb-4">
                            <div class="d-flex align-center mb-2">
                              <v-icon small color="primary" class="mr-2">mdi-camera</v-icon>
                              <span class="body-2">{{ $t("t_verification_evidence") }}</span>
                            </div>
                            <v-btn
                              small
                              color="primary"
                              @click="addVerificationMedia(task, hazard, type)"
                              :disabled="isCompleted"
                              class="mb-2"
                            >
                              <v-icon left small>mdi-camera</v-icon>
                              {{ $t("t_add_evidence_media") }}
                            </v-btn>

                            <div v-if="type.verificationMedia?.length" class="verification-media-grid">
                              <div
                                v-for="media in type.verificationMedia"
                                :key="media.id"
                                class="verification-media-item"
                              >
                                <v-img
                                  :src="media.imageSource"
                                  aspect-ratio="1"
                                  class="grey lighten-2"
                                  @click="previewImage(media.imageSource)"
                                >
                                  <template v-slot:placeholder>
                                    <v-row class="fill-height ma-0" align="center" justify="center">
                                      <v-progress-circular indeterminate></v-progress-circular>
                                    </v-row>
                                  </template>
                                </v-img>
                              </div>
                            </div>
                          </div>
                          <div class="mb-4 mt-2">
                            <v-textarea
                              v-model="type.comments"
                              :label="$t('t_verification_comments')"
                              rows="2"
                              outlined
                              filled
                              auto-grow
                              dense
                              hide-details
                              :disabled="isCompleted"
                            ></v-textarea>
                            <div
                              class="text-right caption"
                              style="opacity: 0.5"
                              v-if="type.commentsUpdatedOn && type.comments"
                            >
                              {{ formatDateTime(type.commentsUpdatedOn) }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="
                    tasks[taskIndex].documents?.length ||
                    tasks[taskIndex].forms?.length ||
                    tasks[taskIndex].trainings?.length
                  "
                >
                  <div class="overline mt-4">{{ $t("t_inline_resources") }}</div>
                  <div class="ml-2">
                    <v-row dense>
                      <v-col cols="12">
                        <div v-if="tasks[taskIndex].documents && tasks[taskIndex].documents.length">
                          <div class="overline">{{ $t("t_documents") }}</div>
                          <div
                            class="d-block mr-2 mb-1"
                            v-for="doc in tasks[taskIndex].documents"
                            :key="'doc-' + doc.id"
                          >
                            <v-btn
                              :block="$vuetify.breakpoint.mobile"
                              color="blue darken-2"
                              @click="openDocumentDialog(doc.id)"
                              class="mr-2 white--text custom-btn"
                              style="
                                white-space: normal;
                                height: auto !important;
                                padding-top: 8px !important;
                                padding-bottom: 8px !important;
                              "
                            >
                              <span style="width: 100%; word-break: break-word">{{ doc.name }}</span>
                            </v-btn>
                          </div>
                        </div>
                        <div v-if="tasks[taskIndex].forms && tasks[taskIndex].forms.length">
                          <div class="overline mt-2">{{ $t("t_forms") }}</div>
                          <div
                            class="d-block mr-2 mb-1"
                            v-for="form in tasks[taskIndex].forms"
                            :key="'form-' + form.id"
                          >
                            <v-btn
                              :block="$vuetify.breakpoint.mobile"
                              :color="getFormSubmissionId(form.id) ? 'blue darken-2' : 'green darken-2'"
                              @click="handleFormClick(form.id)"
                              class="mr-2 white--text custom-btn"
                              style="
                                white-space: normal;
                                height: auto !important;
                                padding-top: 8px !important;
                                padding-bottom: 8px !important;
                              "
                            >
                              <v-icon left v-if="getFormSubmissionId(form.id)" small>mdi-check-circle</v-icon>
                              <span style="width: 100%; word-break: break-word">{{ form.name }}</span>
                            </v-btn>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </div>
              </div>
              <!-- Overall Score (for inspection planners) -->
              <div v-if="plan?.type === 'inspection'" class="mb-1">
                <div class="d-flex align-center mb-2">
                  <span class="subtitle-2 font-weight-bold mr-4">{{ $t("t_overall_score") }}</span>
                  <v-chip :color="overallScore >= 7 ? 'success' : overallScore >= 5 ? 'warning' : 'error'" small>
                    {{ overallScore }}/10
                  </v-chip>
                </div>
                <v-slider
                  v-model="overallScore"
                  :disabled="isCompleted"
                  hide-details
                  min="1"
                  max="10"
                  step="1"
                  thumb-label="always"
                  ticks
                  :color="overallScore >= 7 ? 'success' : overallScore >= 5 ? 'warning' : 'error'"
                  track-color="grey"
                  @change="triggerAutoSave()"
                >
                  <template v-slot:thumb-label="{ value }">
                    {{ value }}
                  </template>
                </v-slider>
              </div>

              <!-- Overall Pass/Fail (for inspection planners) -->
              <div v-if="plan?.type === 'inspection'" class="mb-6">
                <v-radio-group
                  v-model="overallPassFail"
                  :disabled="isCompleted"
                  row
                  hide-details
                  @change="triggerAutoSave()"
                >
                  <template v-slot:label>
                    <span class="subtitle-2 font-weight-bold mr-4">{{ $t("t_overall_pass_fail") }}</span>
                  </template>
                  <v-radio :value="'pass'" color="success">
                    <template v-slot:label>
                      <span class="font-weight-medium">
                        <v-icon small color="success" class="mr-1">mdi-check-circle</v-icon>
                        {{ $t("t_pass") }}
                      </span>
                    </template>
                  </v-radio>
                  <v-radio :value="'fail'" color="error">
                    <template v-slot:label>
                      <span class="font-weight-medium">
                        <v-icon small color="error" class="mr-1">mdi-close-circle</v-icon>
                        {{ $t("t_fail") }}
                      </span>
                    </template>
                  </v-radio>
                </v-radio-group>
              </div>

              <v-textarea
                v-model="overallComments"
                :label="$t('t_overall_comments')"
                rows="2"
                outlined
                filled
                auto-grow
                hide-details
                :disabled="isCompleted"
                @input="triggerAutoSave()"
              ></v-textarea>
              <div class="text-right caption" style="opacity: 0.5" v-if="overallCommentsUpdatedOn && overallComments">
                {{ formatDateTime(overallCommentsUpdatedOn) }}
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col class="d-flex flex-column align-center justify-center" style="min-height: 80vh">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          <div class="mt-4 text-subtitle-1">{{ $t("t_loading_planner") }}</div>
        </v-col>
      </v-row>
    </v-container>

    <!-- Fixed Bottom Action Bar -->
    <v-footer
      v-if="!isCompleted"
      fixed
      padless
      app
      class="action-footer"
      style="height: auto; background-color: transparent !important"
    >
      <v-card
        flat
        width="100%"
        class="px-4 py-2"
        style="border-top: 1px solid rgba(0, 0, 0, 0.12); background-color: transparent !important"
      >
        <div class="d-flex align-center justify-space-between" style="background-color: transparent !important">
          <v-btn v-show="showScrollTop" @click="scrollToTop" icon fab x-small class="mr-2">
            <v-icon>mdi-chevron-up</v-icon>
          </v-btn>

          <v-spacer></v-spacer>

          <!-- Auto-save Indicator -->
          <div v-if="autoSaving" class="caption grey--text d-flex align-center mr-3">
            <v-progress-circular indeterminate size="14" width="2" class="mr-1"></v-progress-circular>
            Saving...
          </div>

          <!-- Action Buttons -->
          <div class="d-flex align-center">
            <v-btn
              @click="submitPlanner(false)"
              :disabled="!isPlanLoaded"
              class="mr-2"
              :style="
                $vuetify.theme.dark ? 'background-color: rgba(0,0,0,0.75)' : 'background-color: rgba(255,255,255,0.75)'
              "
              outlined
              color="primary"
            >
              {{ $t("t_save") }}
            </v-btn>

            <v-btn @click="submitPlanner(true)" color="primary" :disabled="!isPlanLoaded" :outlined="!isPlanLoaded">
              {{ $t("t_submit") }}
              <template v-if="plan?.type !== 'task'"> ({{ Math.round(formProgress) }}%) </template>
            </v-btn>
          </div>
        </div>

        <!-- Progress Bar -->
        <v-progress-linear v-if="plan?.type !== 'task'" :value="formProgress" color="primary" class="mt-2" height="2">
          <template v-slot:default>
            <strong>{{ Math.round(formProgress) }}%</strong>
          </template>
        </v-progress-linear>
      </v-card>
    </v-footer>

    <form-dialog ref="formDialog" @formSubmitted="handleFormSubmission" />
    <document-dialog ref="documentDialog" />
    <v-dialog v-model="showImagePreview" max-width="fit-content" content-class="image-preview-dialog">
      <v-card class="image-preview-card">
        <v-card-title class="pa-2" style="position: absolute; z-index: 999; right: 12px; top: 12px">
          <v-btn icon rounded outlined @click="showImagePreview = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <img :src="selectedImage" class="preview-image" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { PlannerService, AssetService } from "@services";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { DateTime } from "luxon";
import Helpers from "@/mixins/helpers";
import FormDialog from "@/components/forms/FormDialog.vue";
import DocumentDialog from "@/components/documents/DocumentDialog.vue";
import heic2any from "heic2any";

export default {
  name: "Planner",

  components: {
    FormDialog,
    DocumentDialog,
  },

  mixins: [Helpers],

  props: {
    plannerId: {
      type: [Number, String],
      default: null,
    },
    plannerAssignmentId: {
      type: [Number, String],
      default: null,
    },
    plannerAssignmentDueDateId: {
      type: [Number, String],
      default: null,
    },
    plannerSubmissionId: {
      type: [Number, String],
      default: null,
    },
  },

  computed: {
    isPlanLoaded() {
      return Object.keys(this.plan).length > 0;
    },
    newSubmission() {
      return {
        plannerId: this.plannerId,
        plannerAssignmentId: this.plannerAssignmentId,
        plannerDueDateId: this.plannerAssignmentDueDateId,
        name: this.planner.name,
        plannerSchemaJson: JSON.stringify(this.plan),
        plannerSubmissionSchemaJson: JSON.stringify(this.constructSubmissionSchema()),
        submit: false,
      };
    },
    currentStatusCode() {
      return this.submission?.status ? this.submission.status : "1";
    },
    currentStatusText() {
      return this.submission?.status ? this.getStatusText(this.submission.status) : "pending";
    },
    isCompleted() {
      return this.currentStatusText.toLowerCase() === "completed";
    },
    formProgress() {
      if (!this.tasks || !Array.isArray(this.tasks)) return 0;

      // For inspection planners, calculate based on verified criteria
      if (this.plan?.type === "inspection") {
        let totalCriteria = 0;
        let verifiedCriteria = 0;

        this.tasks.forEach((task) => {
          if (task.inspectionCriteria && Array.isArray(task.inspectionCriteria)) {
            totalCriteria += task.inspectionCriteria.length;
            verifiedCriteria += task.inspectionCriteria.filter((criteria) => criteria.status === "verified").length;
          }
        });

        return totalCriteria > 0 ? Math.round((verifiedCriteria / totalCriteria) * 100) : 0;
      }

      // For safety/report planners, calculate based on verified controls
      const allSubTypes = this.tasks.reduce((acc, task) => {
        if (!task.hazards) return acc;
        return acc.concat(
          task.hazards.reduce((hazardAcc, hazard) => {
            if (!hazard.subTypes) return hazardAcc;
            return hazardAcc.concat(hazard.subTypes);
          }, []),
        );
      }, []);

      if (!allSubTypes.length) return 0;

      let totalControls = 0;
      let verifiedControls = 0;

      allSubTypes.forEach((subType) => {
        if (!subType) return;

        const controls = this.getControlItems(subType, "ctrl");
        const training = this.getControlItems(subType, "train");
        const ppe = this.getControlItems(subType, "ppe");
        const mitigation = this.getControlItems(subType, "mit");

        totalControls += controls.length + training.length + ppe.length + mitigation.length;

        if (subType.verifications) {
          verifiedControls += Object.keys(subType.verifications).length;
        }
      });

      return totalControls > 0 ? (verifiedControls / totalControls) * 100 : 0;
    },
  },

  watch: {
    tasks: {
      immediate: true,
      deep: true,
      handler() {
        this.loadImagesForTasks();
      },
    },
  },
  data() {
    return {
      loading: false,
      plan: {},
      planner: {},
      assignment: {},
      submission: {},
      dueDate: {},
      metaData: {},
      tasks: [],
      requiresSafetyCompliance: false,
      overallStatus: "",
      startDate: "",
      completionDate: "",
      overallComments: "",
      overallCommentsUpdatedOn: "",
      overallScore: 5,
      overallPassFail: null,
      showScrollTop: false,
      expandedRegCodes: {},
      imageCache: new Map(),
      showImagePreview: false,
      selectedImage: null,
      taskMedia: new Map(),
      // Auto-save state
      autoSaving: false,
      lastSavedAt: null,
      autoSaveError: null,
      autoSaveTimer: null,
      formSubmissions: [],
    };
  },
  methods: {
    async loadPlanner(plannerId = null) {
      this.loading = true;
      this.showLoadBar();
      plannerId = plannerId ? plannerId : this.$route.params.plannerId;
      try {
        const response = await PlannerService.getPlanner(plannerId);
        const plan = response.data;
        this.plan = plan;
        this.planner = {
          name: plan.name,
          description: plan.description,
          createdOn: plan.createdOn,
          updatedOn: plan.updatedOn,
        };
        if (plan.metaData) {
          this.metaData = JSON.parse(plan.metaData);
        }
        if (plan.schemaJson) {
          const parsed = JSON.parse(plan.schemaJson);
          // Handle both array and object with tasks property
          const tasksArray = Array.isArray(parsed) ? parsed : parsed.tasks || [];
          this.tasks = tasksArray.map((task) => ({
            ...task,
            comments: "",
            completed: false,
            hazards: task.hazards || [],
            inspectionCriteria: (task.inspectionCriteria || []).map((criteria) => ({
              ...criteria,
              status: "pending",
              verifiedOn: null,
              verifiedBy: null,
              comments: "",
              commentsUpdatedOn: null,
              verificationMedia: [],
            })),
          }));
        } else {
          this.tasks = [];
        }
      } catch (error) {
        console.error("Error loading planner:", error);
        this.$snackbar.showMessage({
          content: "Failed to load Planner",
          color: "error",
        });
      } finally {
        this.loading = false;
        this.hideLoadBar();
      }
    },

    async loadAssignment(assignmentId = null) {
      assignmentId = assignmentId ? assignmentId : this.$route.params.plannerAssignmentId;

      try {
        this.loading = true;
        const r = await PlannerService.getPlannerAssignment(assignmentId);
        const assignment = r.data;

        let requiresSafetyCompliance = false;
        if (assignment.dueDates && assignment.dueDates.length > 0) {
          const specificDueDate = assignment.dueDates.find((d) => d.id == Number(this.plannerAssignmentDueDateId));
          if (specificDueDate) {
            requiresSafetyCompliance = specificDueDate.requiresSafetyCompliance || false;
            this.dueDate = specificDueDate;
          } else {
            requiresSafetyCompliance = assignment.dueDates.some((dueDate) => dueDate.requiresSafetyCompliance);
          }
        } else {
          requiresSafetyCompliance = assignment.requiresSafetyCompliance || false;
        }
        this.requiresSafetyCompliance = requiresSafetyCompliance;
        this.assignment = assignment;
      } catch (error) {
        console.error("Error loading planner assignment:", error);
        this.$snackbar.showMessage({
          content: "Failed to load Planner assignment",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    async loadSubmission(submissionId) {
      try {
        this.loading = true;
        const r = await PlannerService.getPlannerSubmission(submissionId);
        this.submission = r.data;
        if (this.submission) {
          const submissionData = JSON.parse(this.submission.plannerSubmissionSchemaJson);

          // Load form submissions
          if (submissionData.formSubmissions && Array.isArray(submissionData.formSubmissions)) {
            this.formSubmissions = submissionData.formSubmissions;
          }

          // Load overall comments and inspection scores
          this.overallComments = submissionData.overallComments || "";
          this.overallCommentsUpdatedOn = submissionData.overallCommentsUpdatedOn;

          // Load inspection-specific fields
          if (this.plan?.type === "inspection") {
            this.overallScore = submissionData.overallScore !== undefined ? submissionData.overallScore : 5;
            this.overallPassFail = submissionData.overallPassFail || null;
          }

          // Load task data
          if (submissionData.tasks) {
            submissionData.tasks.forEach((submittedTask) => {
              const task = this.tasks.find((t) => t.id === submittedTask.id);
              if (task) {
                task.status = submittedTask.status || "Incomplete";
                task.completedOn = submittedTask.completedOn || null;
                task.comments = submittedTask.comments || "";
                task.commentsUpdatedOn = submittedTask.commentsUpdatedOn;
              }
            });
          }

          // Load inspection criteria data (for inspection type planners)
          if (submissionData.inspectionCriteria && this.plan?.type === "inspection") {
            for (const submittedCriteria of submissionData.inspectionCriteria) {
              const task = this.tasks.find((t) => t.id === submittedCriteria.taskId);
              if (task && task.inspectionCriteria) {
                // Match criteria by name to be more robust
                const criteria = task.inspectionCriteria.find((c) => c.name === submittedCriteria.name);
                if (criteria) {
                  criteria.status = submittedCriteria.status || "pending";
                  criteria.verifiedOn = submittedCriteria.verifiedOn || null;
                  criteria.verifiedBy = submittedCriteria.verifiedBy || null;
                  criteria.comments = submittedCriteria.comments || "";
                  criteria.commentsUpdatedOn = submittedCriteria.commentsUpdatedOn;

                  // Load verification media
                  if (submittedCriteria.verificationMedia?.length) {
                    const mediaArray = [];
                    for (const media of submittedCriteria.verificationMedia) {
                      try {
                        const assetResponse = await AssetService.downloadAsset({
                          assetId: media.assetId,
                          token: media.publicId,
                        });

                        if (assetResponse?.data) {
                          const imageUrl =
                            assetResponse.data.canvasThumbUrl ||
                            assetResponse.data.canvasUrl ||
                            assetResponse.data.thumbnailUrl ||
                            assetResponse.data.downloadUrl;
                          if (imageUrl) {
                            const imageBlob = await fetch(imageUrl).then((r) => r.blob());
                            const base64 = await this.blobToBase64(imageBlob);

                            mediaArray.push({
                              id: Date.now(),
                              assetId: media.assetId,
                              publicId: media.publicId,
                              imageSource: base64,
                              createdAt: media.date,
                              taskId: task.id,
                              criteriaId: submittedCriteria.criteriaId,
                              criteriaName: criteria.name,
                            });
                          }
                        }
                      } catch (error) {
                        console.error("Error loading criteria media:", error);
                      }
                    }
                    this.$set(criteria, "verificationMedia", mediaArray);
                  }
                }
              }
            }
          }

          // Load hazard and subtype data
          if (submissionData.hazards) {
            for (const submittedHazard of submissionData.hazards) {
              const matchingTasks = this.tasks.filter((task) =>
                task.hazards.some((h) => h.type === submittedHazard.type),
              );

              for (const task of matchingTasks) {
                const matchingHazard = task.hazards.find((h) => h.type === submittedHazard.type);
                if (matchingHazard) {
                  // Load hazard comments
                  matchingHazard.comments = submittedHazard.comments || "";
                  matchingHazard.commentsUpdatedOn = submittedHazard.commentsUpdatedOn;

                  for (const submittedSubType of submittedHazard.subTypes) {
                    const matchingSubType = matchingHazard.subTypes.find((st) => st.name === submittedSubType.name);
                    if (matchingSubType) {
                      // Load verifications
                      if (submittedSubType.verifications) {
                        this.$set(matchingSubType, "verifications", { ...submittedSubType.verifications });
                      }

                      // Load subtype comments
                      matchingSubType.comments = submittedSubType.comments || "";
                      matchingSubType.commentsUpdatedOn = submittedSubType.commentsUpdatedOn;

                      // Load verification media
                      if (submittedSubType.verificationMedia?.length) {
                        const mediaArray = [];
                        for (const media of submittedSubType.verificationMedia) {
                          try {
                            const assetResponse = await AssetService.downloadAsset({
                              assetId: media.assetId,
                              token: media.publicId,
                            });

                            if (assetResponse?.data) {
                              // Use thumbnailUrl if available, otherwise use downloadUrl
                              const imageUrl =
                                assetResponse.data.canvasThumbUrl ||
                                assetResponse.data.canvasUrl ||
                                assetResponse.data.thumbnailUrl ||
                                assetResponse.data.downloadUrl;
                              if (imageUrl) {
                                const imageBlob = await fetch(imageUrl).then((r) => r.blob());
                                const base64 = await this.blobToBase64(imageBlob);

                                mediaArray.push({
                                  id: Date.now(),
                                  assetId: media.assetId,
                                  publicId: media.publicId,
                                  imageSource: base64,
                                  createdAt: media.date,
                                  taskId: media.taskId,
                                  hazardId: media.hazardId,
                                  subTypeId: media.subTypeId,
                                  hazardType: media.hazardType,
                                  subTypeName: media.subTypeName,
                                });
                              }
                            }
                          } catch (error) {
                            console.error("Error loading verification media:", error);
                          }
                        }

                        if (mediaArray.length) {
                          this.$set(matchingSubType, "verificationMedia", mediaArray);
                        }
                      }
                    }
                  }
                }
              }
            }
          }

          if (submissionData.tasks) {
            for (const submittedTask of submissionData.tasks) {
              if (submittedTask.media?.length) {
                const mediaArray = [];

                for (const media of submittedTask.media) {
                  try {
                    const assetResponse = await AssetService.downloadAsset({
                      assetId: media.assetId,
                    });

                    if (assetResponse?.data) {
                      // Use thumbnailUrl if available, otherwise use downloadUrl
                      const imageUrl =
                        assetResponse.data.canvasThumbUrl ||
                        assetResponse.data.canvasUrl ||
                        assetResponse.data.thumbnailUrl ||
                        assetResponse.data.downloadUrl;

                      if (imageUrl) {
                        const imageBlob = await fetch(imageUrl).then((r) => r.blob());
                        const base64 = await this.blobToBase64(imageBlob);

                        mediaArray.push({
                          id: Date.now(),
                          assetId: media.assetId,
                          imageSource: base64,
                          createdAt: media.createdAt,
                        });
                      }
                    }
                  } catch (error) {
                    console.error("Error loading media:", error);
                  }
                }

                if (mediaArray.length) {
                  this.taskMedia.set(submittedTask.id, mediaArray);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Error loading submission:", error);
        this.$snackbar.showMessage({
          content: "Failed to load submission",
          color: "error",
        });
      } finally {
        this.loading = false;

        // For safety and report planners, update task completion based on loaded control verifications
        if (this.plan?.type === "safety" || this.plan?.type === "report") {
          this.$nextTick(() => {
            this.updateTaskCompletionFromControls();
          });
        }
      }
    },

    constructSubmissionSchema() {
      const timestamp = DateTime.now().toISO();
      const currentSubmission = this.submission?.plannerSubmissionSchemaJson
        ? JSON.parse(this.submission.plannerSubmissionSchemaJson)
        : null;

      return {
        overallStatus: this.overallStatus || "",
        startDate: this.startDate || timestamp,
        completionDate: this.completionDate || "",
        tasks: this.tasks.map((task) => {
          const currentTask = currentSubmission?.tasks?.find((t) => t.id === task.id);
          return {
            id: task.id,
            status: task.status || "Incomplete",
            comments: task.comments || "",
            commentsUpdatedOn:
              task.comments !== currentTask?.comments ? timestamp : currentTask?.commentsUpdatedOn || timestamp,
            completedOn: task.completedOn || null,
            media: [],
          };
        }),
        hazards: this.tasks.flatMap((task) =>
          task.hazards.map((hazard) => {
            const currentHazard = currentSubmission?.hazards?.find((h) => h.type === hazard.type);
            return {
              hazardId: hazard.hazardId,
              type: hazard.type,
              comments: hazard.comments || "",
              commentsUpdatedOn:
                hazard.comments !== currentHazard?.comments ? timestamp : currentHazard?.commentsUpdatedOn || timestamp,
              subTypes: hazard.subTypes.map((subType) => {
                const currentSubType = currentHazard?.subTypes?.find((st) => st.name === subType.name);

                // Get all control items for this subtype
                const controls = this.getControlItems(subType, "ctrl");
                const training = this.getControlItems(subType, "train");
                const ppe = this.getControlItems(subType, "ppe");
                const mitigation = this.getControlItems(subType, "mit");
                const allControls = [...controls, ...training, ...ppe, ...mitigation];

                // Build status object for each control
                const statusObj = {};
                allControls.forEach((control) => {
                  statusObj[control.id] = subType.verifications?.[control.id] ? "done" : "pend";
                });

                return {
                  subTypeId: subType.subTypeId,
                  name: subType.name,
                  verifications: subType.verifications || {},
                  status: statusObj,
                  verificationMedia: (subType.verificationMedia || []).map((media) => ({
                    assetId: media.assetId,
                    publicId: media.publicId,
                    date: media.createdAt,
                    taskId: task.id,
                    hazardId: hazard.hazardId,
                    subTypeId: subType.subTypeId,
                    hazardType: hazard.type,
                    subTypeName: subType.name,
                  })),
                  comments: subType.comments || "",
                  commentsUpdatedOn:
                    subType.comments !== currentSubType?.comments
                      ? timestamp
                      : currentSubType?.commentsUpdatedOn || null,
                };
              }),
            };
          }),
        ),
        inspectionCriteria:
          this.plan?.type === "inspection"
            ? this.tasks.flatMap((task) =>
                (task.inspectionCriteria || []).map((criteria, criteriaIndex) => {
                  const criteriaId = `criteria-${criteriaIndex}`;
                  // Match by taskId and name for more robust matching
                  const currentCriteria = currentSubmission?.inspectionCriteria?.find(
                    (c) => c.taskId === task.id && c.name === criteria.name,
                  );

                  return {
                    taskId: task.id,
                    criteriaId: criteriaId,
                    name: criteria.name,
                    priority: criteria.priority,
                    status: criteria.status || "pending",
                    verifiedOn: criteria.verifiedOn || null,
                    verifiedBy: criteria.verifiedBy || null,
                    comments: criteria.comments || "",
                    commentsUpdatedOn:
                      criteria.comments !== currentCriteria?.comments
                        ? timestamp
                        : currentCriteria?.commentsUpdatedOn || null,
                    regulatoryCodes: criteria.regulatoryCodes || [],
                    acceptanceCriteria: criteria.acceptanceCriteria || "",
                    testMethod: criteria.testMethod || "",
                    toleranceValues: criteria.toleranceValues || "",
                    documentation: criteria.documentation || "",
                    correctiveAction: criteria.correctiveAction || "",
                    verificationMedia: (criteria.verificationMedia || []).map((media) => ({
                      assetId: media.assetId,
                      publicId: media.publicId,
                      date: media.createdAt,
                      taskId: task.id,
                      criteriaId: criteriaId,
                      criteriaName: criteria.name,
                    })),
                  };
                }),
              )
            : [],
        overallComments: this.overallComments || "",
        overallCommentsUpdatedOn:
          this.overallComments !== currentSubmission?.overallComments
            ? timestamp
            : currentSubmission?.overallCommentsUpdatedOn || timestamp,
        overallScore: this.plan?.type === "inspection" ? this.overallScore : undefined,
        overallPassFail: this.plan?.type === "inspection" ? this.overallPassFail : undefined,
        logIts: [],
        formSubmissions: this.formSubmissions,
        updatedOn: timestamp,
        completionPercentage: this.formProgress,
      };
    },

    async loadImageSource(evidence) {
      if (!evidence || evidence.imageSource) return;

      try {
        if (this.imageCache.has(evidence.assetId)) {
          this.$set(evidence, "imageSource", this.imageCache.get(evidence.assetId));
          return;
        }

        let imageSource;
        const response = await AssetService.downloadAsset({
          assetId: evidence.assetId,
          token: evidence.publicId,
        });

        if (response?.data?.downloadUrl) {
          const imageUrl =
            response.data.canvasThumbUrl ||
            response.data.canvasUrl ||
            response.data.thumbnailUrl ||
            response.data.downloadUrl;
          const imageBlob = await fetch(imageUrl).then((r) => r.blob());
          const base64 = await this.blobToBase64(imageBlob);
          imageSource = `data:image/jpeg;base64,${base64.split(",")[1]}`;
        }

        if (imageSource) {
          this.imageCache.set(evidence.assetId, imageSource);
          this.$set(evidence, "imageSource", imageSource);
        }
      } catch (error) {
        console.error("Error loading image:", error);
      }
    },

    blobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    },

    // Modify your existing watch or created hook to load images
    async loadImagesForTasks() {
      if (!this.tasks) return;

      for (const task of this.tasks) {
        if (task.hazards) {
          for (const hazard of task.hazards) {
            if (hazard.sourceEvidence?.images) {
              for (const evidence of hazard.sourceEvidence.images) {
                await this.loadImageSource(evidence);
              }
            }
          }
        }
      }
    },
    handleImageError(evidence) {
      console.error("Failed to load image:", evidence);
      this.$set(evidence, "imageLoadError", true);
    },
    calculateCompletionPercentage() {
      const totalTasks = this.tasks.length;
      const completedTasks = this.tasks.filter((t) => t.completed).length;
      return totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    },

    async saveToServer(submit) {
      // Priority: 1) this.submission.id, 2) route params
      const routeSubmissionId = this.$route.params.plannerSubmissionId;
      const hasExistingSubmission =
        (this.submission?.id && this.submission.id !== 0 && this.submission.id !== "0") ||
        (routeSubmissionId && routeSubmissionId !== "0" && routeSubmissionId !== 0);

      if (!this.submission?.id && routeSubmissionId) {
        if (!this.submission) this.submission = {};
        this.submission.id = parseInt(routeSubmissionId);
      }
      const submissionData = {
        ...this.newSubmission,
        submit: submit,
        id: this.submission?.id,
      };

      if (submissionData.plannerDueDateId === 0 || submissionData.plannerDueDateId === "0") {
        submissionData.plannerDueDateId = null;
      }
      if (hasExistingSubmission) {
        await PlannerService.patchPlannerSubmission(submissionData);
      } else {
        const response = await PlannerService.createPlannerSubmission(submissionData);
        // Update submission ID for future auto-saves
        const newId = response.data?.submissionId;
        if (newId) {
          if (!this.submission) this.submission = {};
          this.submission.id = newId;

          const plannerId = this.$route.params.plannerId;
          const plannerAssignmentId = this.$route.params.plannerAssignmentId;
          const plannerDueDateId = this.$route.params.plannerAssignmentDueDateId || 0;

          this.$router
            .replace({
              name: this.$route.name,
              params: {
                plannerId,
                plannerAssignmentId,
                plannerAssignmentDueDateId: plannerDueDateId,
                plannerSubmissionId: newId,
              },
              query: this.$route.query,
            })
            .catch((err) => {
              // Silently ignore NavigationDuplicated errors
              if (err.name !== "NavigationDuplicated") {
                console.error("Error updating route with submission ID:", err);
              }
            });
        }
      }
    },

    async autoSave() {
      if (this.isCompleted) return;

      try {
        this.autoSaving = true;
        this.autoSaveError = null;

        await this.saveToServer(false); // false = don't submit, just save

        this.lastSavedAt = DateTime.now();
        this.autoSaving = false;
      } catch (error) {
        console.error("Auto-save failed:", error);
        this.autoSaveError = error.message || "Save failed";
        this.autoSaving = false;
      }
    },

    triggerAutoSave(delay = 2000) {
      // Cancel any existing pending auto-save
      if (this.autoSaveTimer) {
        clearTimeout(this.autoSaveTimer);
      }

      // Schedule auto-save with custom delay
      this.autoSaveTimer = setTimeout(() => {
        this.autoSave();
      }, delay);
    },

    async submitPlanner(submit) {
      try {
        // Cancel any pending auto-save
        if (this.autoSaveTimer) {
          clearTimeout(this.autoSaveTimer);
          this.autoSaveTimer = null;
        }

        await this.saveToServer(submit);

        this.$snackbar.showMessage({
          content: submit ? "Planner submitted successfully" : "Planner saved successfully",
          color: "success",
        });

        if (submit) {
          this.$router.push("/planners");
        } else {
          // Update last saved time for manual saves
          this.lastSavedAt = DateTime.now();
        }
      } catch (error) {
        console.error("Error submitting planner:", error);
        this.$snackbar.showMessage({
          content: "Failed to submit planner",
          color: "error",
        });
      }
    },

    updateHazardStatus(taskIndex, hazardIndex, subTypeIndex, checked) {
      const subType = this.tasks[taskIndex].hazards[hazardIndex].subTypes[subTypeIndex];
      this.$set(subType, "trainingConfirmed", checked);
    },

    getRiskRating(likelihood, impact) {
      likelihood = Math.max(1, Math.min(5, likelihood));
      impact = Math.max(1, Math.min(5, impact));
      return Math.round(((likelihood * impact - 1) / 24) * 100);
    },

    getStatusColor(status) {
      return (
        {
          compliant: "success",
          "non-compliant": "error",
          "needs-review": "warning",
        }[status] || (this.$vuetify.theme.dark ? "grey lighten-2" : "grey darken-2")
      );
    },

    splitCommaString(input) {
      if (!input) return [];

      const items = input.split(/,\s*(?=[A-Z]+\d+\|)/);

      return items
        .map((item) => {
          // Extract identifier and label from each item
          const parts = item.trim().match(/^([A-Z]+\d+)\|(.+)$/);
          if (parts) {
            return {
              id: parts[1], // The identifier (e.g., CM001)
              label: parts[2].trim(), // The label text, preserving any commas within
            };
          }
          return {
            id: item.trim(),
            label: item.trim(),
          };
        })
        .filter((item) => item.id || item.label); // Remove any empty items
    },

    setTrackColor(val) {
      const colors = ["light-blue", "light-green", "yellow", "orange", "red"];
      return colors[Math.min(Math.floor(val), 4)];
    },

    getFormSubmissionId(formId) {
      const submission = this.formSubmissions.find((fs) => fs.formId === formId);
      return submission ? submission.submissionId : null;
    },

    handleFormClick(formId) {
      const submissionId = this.getFormSubmissionId(formId);
      if (submissionId) {
        this.$router.push({ name: "submission", params: { id: submissionId } });
      } else {
        this.openFormDialog(formId, this.plannerId, this.plannerAssignmentId, this.plannerAssignmentDueDateId);
      }
    },

    openFormDialog(formId, plannerId, plannerAssignmentId, plannerAssignmentDueDateId) {
      this.$refs.formDialog.openDialog(formId, plannerId, plannerAssignmentId, plannerAssignmentDueDateId);
    },

    handleFormSubmission(submissionData) {
      // Check if this form submission already exists
      const existingIndex = this.formSubmissions.findIndex((fs) => fs.formId === submissionData.formId);

      if (existingIndex >= 0) {
        // Update existing submission
        this.formSubmissions[existingIndex] = {
          formId: submissionData.formId,
          submissionId: submissionData.submissionId,
        };
      } else {
        // Add new submission
        this.formSubmissions.push({
          formId: submissionData.formId,
          submissionId: submissionData.submissionId,
        });
      }

      // Trigger auto-save to persist the form submission reference
      this.triggerAutoSave(100);
    },

    openDocumentDialog(documentId) {
      this.$refs.documentDialog.openDialog(documentId);
    },

    handleScroll() {
      this.showScrollTop = window.scrollY > 500;
    },

    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },

    previewImage(imageSource) {
      this.selectedImage = imageSource;
      this.showImagePreview = true;
    },

    getSentimentColor(sentiment) {
      // Handle string sentiment values
      if (sentiment === "") return this.$vuetify.theme.dark ? "grey darken-2" : "grey lighten-2";

      // Convert to number if string
      const sentimentNum = Number(sentiment);
      if (isNaN(sentimentNum)) return this.$vuetify.theme.dark ? "grey darken-2" : "grey lighten-2";

      const colors = {
        2: "green",
        1: "light-green",
        0: this.$vuetify.theme.dark ? "grey darken-2" : "grey lighten-2",
        "-1": "orange",
        "-2": "red",
      };
      return colors[sentimentNum.toString()] || (this.$vuetify.theme.dark ? "grey darken-2" : "grey lighten-2");
    },

    getSentimentText(sentiment) {
      const sentimentMap = {
        2: "Very Positive",
        1: "Positive",
        0: "Neutral",
        "-1": "Negative",
        "-2": "Very Negative",
      };
      return sentimentMap[sentiment] || "Unknown";
    },

    getSentimentIcon(sentiment) {
      const icons = {
        "-2": "mdi-emoticon-angry-outline",
        "-1": "mdi-emoticon-sad-outline",
        0: "mdi-emoticon-neutral-outline",
        1: "mdi-emoticon-happy-outline",
        2: "mdi-emoticon-excited-outline",
      };
      return icons[sentiment.toString()] || "mdi-emoticon-neutral-outline";
    },

    getSeverityColor(severity) {
      const colors = {
        1: "green",
        2: "yellow darken-2",
        3: "orange",
        4: "red",
      };
      return colors[severity] || "grey";
    },

    async addVerificationMedia(task, hazard, type) {
      try {
        const image = await Camera.getPhoto({
          quality: 90,
          allowEditing: false,
          resultType: CameraResultType.Base64,
          source: this.$platform !== "web" ? CameraSource.Prompt : CameraSource.Photos,
        });

        let base64String = image.base64String;

        // Check if image is HEIC
        if (image.base64String.startsWith("AAAA") || image.format?.toLowerCase() === "heic") {
          // Convert HEIC to JPEG
          const convertedBase64 = await this.convertHeicToJpeg(image);
          if (!convertedBase64) {
            throw new Error("Failed to convert HEIC image");
          }
          base64String = convertedBase64.split(",")[1]; // Remove the data URL prefix
        }

        // Process thumbnail
        const thumbnailBase64 = await this.processImage(base64String, {
          maxDimension: 600,
          quality: 0.7,
        });

        // Convert base64 to Buffer for upload
        const base64Data = new Buffer.from(base64String, "base64");
        const thumbnailBuffer = new Buffer.from(thumbnailBase64, "base64");

        const photoExt = "jpg";
        const photoName = `verification-media_${new Date().getTime()}.${photoExt}`;

        const payload = {
          name: photoName,
          description: "",
          originalFileName: photoName,
          mimeType: "image/jpeg",
          cacheable: true,
          permitCompany: true,
          includeThumbnail: true,
        };

        const response = await AssetService.uploadAssetBegin(payload);

        // Upload original image
        await fetch(response.data.uploadUrl, {
          method: "PUT",
          headers: {
            "Content-Type": "image/jpeg",
            "Content-Encoding": "base64",
          },
          body: base64Data,
        });

        // Upload thumbnail
        await fetch(response.data.thumbnailUploadUrl, {
          method: "PUT",
          headers: {
            "Content-Type": "image/jpeg",
            "Content-Encoding": "base64",
          },
          body: thumbnailBuffer,
        });

        await AssetService.uploadAssetComplete({
          assetId: response.data.assetId,
          hasThumbnail: true,
        });

        if (!type.verificationMedia) {
          this.$set(type, "verificationMedia", []);
        }

        const newMedia = {
          id: Date.now(),
          assetId: response.data.assetId,
          publicId: response.data.assetGuid,
          imageSource: `data:image/jpeg;base64,${base64String}`,
          createdAt: new Date().toISOString(),
          taskId: task.id,
          hazardId: hazard.hazardId,
          subTypeId: type.subTypeId,
          hazardType: hazard.type,
          subTypeName: type.name,
        };

        type.verificationMedia.push(newMedia);

        // Trigger auto-save with short delay (100ms) after adding media
        this.triggerAutoSave(100);
      } catch (error) {
        console.error("Error adding verification media:", error);
        throw error;
      }
    },

    // Helper methods for HEIC conversion
    async convertHeicToJpeg(image) {
      try {
        // Convert base64 to blob
        const base64Data = image.base64String;
        const blob = await fetch(`data:image/heic;base64,${base64Data}`).then((res) => res.blob());

        // Convert HEIC to JPEG
        const jpegBlob = await heic2any({
          blob: blob,
          toType: "image/jpeg",
          quality: 0.9,
        });

        // Convert back to base64
        const base64String = await this.blobToBase64(jpegBlob);
        return base64String;
      } catch (error) {
        console.error("Error converting HEIC to JPEG:", error);
        throw error;
      }
    },

    base64ToBlob(base64, type) {
      const byteString = atob(base64);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);

      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }

      return new Blob([ab], { type });
    },

    updateVerification(type, controlId, checked) {
      if (!type.verifications) {
        this.$set(type, "verifications", {});
      }
      if (!type.status) {
        this.$set(type, "status", {});
      }

      if (checked) {
        this.$set(type.verifications, controlId, DateTime.now().toISO());
        this.$set(type.status, controlId, "done");
      } else {
        this.$delete(type.verifications, controlId);
        this.$set(type.status, controlId, "pend");
      }

      this.$forceUpdate();

      if (this.plan?.type === "safety" || this.plan?.type === "report") {
        this.$nextTick(() => {
          this.updateTaskCompletionFromControls();
        });
      }

      this.triggerAutoSave();
    },

    parseControlItem(item) {
      if (!item) return { id: "", label: "" };
      // If item is already in the correct format, return it
      if (typeof item === "object" && item.id && item.label) {
        return item;
      }
      // For backward compatibility, handle string input
      if (typeof item === "string") {
        const parts = item.split("|");
        return {
          id: parts[0] || item,
          label: parts[1] || item,
        };
      }
      // Fallback
      return { id: String(item), label: String(item) };
    },

    getControlItems(type, field) {
      const items = this.splitCommaString(
        field === "train"
          ? type.requiredTraining
          : field === "ctrl"
          ? type.controlMeasures
          : field === "ppe"
          ? type.ppe
          : type.mitigationStrategies,
      );
      return items.map((item) => this.parseControlItem(item));
    },

    getVerificationStatus(field, subType) {
      if (!subType.verifications) return "pend";
      const items = this.getControlItems(subType, field);
      if (!items.length) return "none";

      const verifiedCount = items.filter((item) => subType.verifications[item.id]).length;
      return verifiedCount === 0 ? "pend" : verifiedCount < items.length ? "part" : "done";
    },

    taskHasRiskControls(task) {
      // Check if a task has any hazards with controls
      if (!task.hazards || task.hazards.length === 0) return false;

      for (const hazard of task.hazards) {
        if (!hazard.subTypes || hazard.subTypes.length === 0) continue;

        for (const subType of hazard.subTypes) {
          const controls = this.getControlItems(subType, "ctrl");
          const training = this.getControlItems(subType, "train");
          const ppe = this.getControlItems(subType, "ppe");
          const mitigation = this.getControlItems(subType, "mit");
          const allControls = [...controls, ...training, ...ppe, ...mitigation];

          // If this subtype has any controls, the task has risk controls
          if (allControls.length > 0) return true;
        }
      }

      return false;
    },

    areAllTaskControlsVerified(task) {
      // Only for safety planners - check if all hazard controls are verified
      if (!task.hazards || task.hazards.length === 0) return false;

      for (const hazard of task.hazards) {
        if (!hazard.subTypes || hazard.subTypes.length === 0) continue;

        for (const subType of hazard.subTypes) {
          // Get all control items for this subtype
          const controls = this.getControlItems(subType, "ctrl");
          const training = this.getControlItems(subType, "train");
          const ppe = this.getControlItems(subType, "ppe");
          const mitigation = this.getControlItems(subType, "mit");
          const allControls = [...controls, ...training, ...ppe, ...mitigation];

          // If there are no controls, skip this subtype
          if (allControls.length === 0) continue;

          // Check if all controls are verified
          if (!subType.verifications) return false;

          const allVerified = allControls.every((control) => subType.verifications[control.id]);
          if (!allVerified) return false;
        }
      }

      return true;
    },

    updateTaskCompletionFromControls() {
      // For safety and report planners, automatically set task status based on control verification
      // Only for tasks that have risk controls
      if (this.plan?.type !== "safety" && this.plan?.type !== "report") return;

      this.tasks.forEach((task) => {
        // Only update completion for tasks with risk controls
        if (!this.taskHasRiskControls(task)) return;

        const allVerified = this.areAllTaskControlsVerified(task);

        if (allVerified && task.status !== "done") {
          // All controls verified - mark task as complete
          task.status = "done";
          task.completedOn = DateTime.local().toUTC().toISO();
        } else if (!allVerified && task.status === "done") {
          // Not all controls verified - mark task as incomplete
          task.status = "Incomplete";
          task.completedOn = null;
        }
      });
    },

    updateTaskStatus(task, status) {
      if (status === "done") {
        task.completedOn = DateTime.local().toUTC().toISO();
      } else {
        task.completedOn = null;
      }
      this.$forceUpdate();

      // Trigger auto-save with standard delay (2000ms)
      this.triggerAutoSave();
    },

    updateCriteriaStatus(task, criteria, status) {
      if (status === "verified") {
        criteria.verifiedOn = DateTime.local().toUTC().toISO();
        // Initialize verificationMedia if it doesn't exist
        if (!criteria.verificationMedia) {
          this.$set(criteria, "verificationMedia", []);
        }
      } else {
        criteria.verifiedOn = null;
      }
      this.$forceUpdate();

      // Trigger auto-save with standard delay (2000ms)
      this.triggerAutoSave();
    },

    toggleCriteriaStatus(task, criteria, event) {
      // Prevent double-toggling if clicking directly on checkbox
      if (event && event.target.closest(".v-input--selection-controls")) {
        return;
      }
      const newStatus = criteria.status === "verified" ? "pending" : "verified";
      this.updateCriteriaStatus(task, criteria, newStatus);
    },

    async addCriteriaMedia(task, criteria) {
      try {
        const image = await Camera.getPhoto({
          quality: 90,
          allowEditing: false,
          resultType: CameraResultType.Base64,
          source: this.$platform !== "web" ? CameraSource.Prompt : CameraSource.Photos,
        });

        let base64String = image.base64String;

        // Check if image is HEIC
        if (image.base64String.startsWith("AAAA") || image.format?.toLowerCase() === "heic") {
          const convertedBase64 = await this.convertHeicToJpeg(image);
          if (!convertedBase64) {
            throw new Error("Failed to convert HEIC image");
          }
          base64String = convertedBase64.split(",")[1];
        }

        // Process thumbnail
        const thumbnailBase64 = await this.processImage(base64String, {
          maxDimension: 600,
          quality: 0.7,
        });

        // Convert base64 to Buffer for upload
        const base64Data = new Buffer.from(base64String, "base64");
        const thumbnailBuffer = new Buffer.from(thumbnailBase64, "base64");

        const photoExt = "jpg";
        const photoName = `criteria-media_${new Date().getTime()}.${photoExt}`;

        const payload = {
          name: photoName,
          description: "",
          originalFileName: photoName,
          mimeType: "image/jpeg",
          cacheable: true,
          permitCompany: true,
          includeThumbnail: true,
        };

        const response = await AssetService.uploadAssetBegin(payload);

        // Upload original image
        await fetch(response.data.uploadUrl, {
          method: "PUT",
          headers: {
            "Content-Type": "image/jpeg",
            "Content-Encoding": "base64",
          },
          body: base64Data,
        });

        // Upload thumbnail
        await fetch(response.data.thumbnailUploadUrl, {
          method: "PUT",
          headers: {
            "Content-Type": "image/jpeg",
            "Content-Encoding": "base64",
          },
          body: thumbnailBuffer,
        });

        await AssetService.uploadAssetComplete({
          assetId: response.data.assetId,
          hasThumbnail: true,
        });

        if (!criteria.verificationMedia) {
          this.$set(criteria, "verificationMedia", []);
        }

        // Find criteria index for criteriaId
        const criteriaIndex = task.inspectionCriteria.findIndex((c) => c.name === criteria.name);
        const criteriaId = criteriaIndex >= 0 ? `criteria-${criteriaIndex}` : `criteria-${Date.now()}`;

        const newMedia = {
          id: Date.now(),
          assetId: response.data.assetId,
          publicId: response.data.assetGuid,
          imageSource: `data:image/jpeg;base64,${base64String}`,
          createdAt: new Date().toISOString(),
          taskId: task.id,
          criteriaId: criteriaId,
          criteriaName: criteria.name,
        };

        criteria.verificationMedia.push(newMedia);

        // Trigger auto-save with short delay (100ms) after adding media
        this.triggerAutoSave(100);
      } catch (error) {
        console.error("Error adding criteria media:", error);
        throw error;
      }
    },

    getPriorityColor(priority) {
      const colors = {
        critical: "error",
        high: "warning",
        medium: "info",
        low: "success",
      };
      return colors[priority?.toLowerCase()] || "grey";
    },

    formatMultiLineText(text) {
      if (!text) return "";
      // Convert \n escape sequences to actual newlines
      return String(text).replace(/\\n/g, "\n");
    },

    getMultiLineList(text) {
      if (!text) return [];
      // Convert \n escape sequences to actual newlines, then split
      const formatted = String(text).replace(/\\n/g, "\n");
      return formatted.split("\n").filter((line) => line.trim().length > 0);
    },

    shouldTruncateRegCode(text) {
      if (!text) return false;
      return String(text).length > 80;
    },

    getTruncatedText(text, maxLength = 80) {
      if (!text) return "";
      const str = String(text);
      return str.length > maxLength ? str.substring(0, maxLength) + "..." : str;
    },

    toggleRegCodeExpansion(regCodeKey) {
      this.$set(this.expandedRegCodes, regCodeKey, !this.expandedRegCodes[regCodeKey]);
    },

    isRegCodeExpanded(regCodeKey) {
      return !!this.expandedRegCodes[regCodeKey];
    },
  },

  async mounted() {
    await this.loadPlanner();
    await this.loadAssignment();
    if (this.$route.params.plannerSubmissionId) {
      this.loadSubmission(this.$route.params.plannerSubmissionId);
    }
    window.addEventListener("scroll", this.handleScroll);
  },

  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  },

  async beforeRouteLeave(to, from, next) {
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
      await this.autoSave();
    }
    next();
  },
};
</script>

<style lang="scss">
.planner {
  // Planner header styles
  h2 {
    font-size: 1.5rem;
    line-height: 1.3;
    word-break: break-word;
  }

  .overline {
    display: block;
    line-height: 1.4;
  }

  // Mobile optimization for header
  @media (max-width: 600px) {
    h2 {
      font-size: 1.25rem;
    }
  }

  .caption.risk-rating {
    font-size: 0.6rem !important;
    line-height: 1rem;
  }

  .inspection-criteria-item {
    padding: 16px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.02);
    margin-bottom: 16px;

    .theme--dark & {
      background: rgba(255, 255, 255, 0.05);
    }

    .regulatory-code-item {
      border-left: 3px solid #2196f3;
      padding-left: 12px;
    }

    .verification-media-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
      gap: 12px;
      margin-top: 12px;

      .verification-media-item {
        border-radius: 4px;
        overflow: hidden;
        cursor: pointer;
        transition: transform 0.2s;

        &:hover {
          transform: scale(1.05);
        }
      }
    }
  }

  .multi-line-list {
    margin: 8px 0;
    padding-left: 5px;
    list-style: none;

    li {
      position: relative;
      padding-left: 12px;
      margin-bottom: 4px;
      line-height: 1.5;

      &::before {
        content: "•";
        position: absolute;
        left: 0;
        color: currentColor;
        font-weight: bold;
        font-size: 1.2em;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .pass-inspection-container {
    padding: 12px 16px;
    border-radius: 8px;
    // Unchecked state - light yellow for light mode
    background: rgba(255, 247, 175, 0.258);
    border: 1px solid rgba(255, 235, 59, 0.3);
    transition: all 0.2s ease;

    .theme--dark & {
      // Unchecked state - dark yellow for dark mode
      background: rgba(255, 193, 7, 0.12);
      border-color: rgba(255, 193, 7, 0.25);
    }

    &:hover:not(.is-disabled) {
      background: rgba(255, 235, 59, 0.2);
      border-color: rgba(255, 235, 59, 0.4);

      .theme--dark & {
        background: rgba(255, 193, 7, 0.18);
        border-color: rgba(255, 193, 7, 0.35);
      }
    }

    // Checked state - green (unchanged)
    &.is-verified {
      background: rgba(76, 175, 80, 0.15);
      border-color: rgba(76, 175, 80, 0.4);

      .theme--dark & {
        background: rgba(76, 175, 80, 0.2);
        border-color: rgba(76, 175, 80, 0.5);
      }

      &:hover:not(.is-disabled) {
        background: rgba(76, 175, 80, 0.2);
        border-color: rgba(76, 175, 80, 0.5);

        .theme--dark & {
          background: rgba(76, 175, 80, 0.25);
          border-color: rgba(76, 175, 80, 0.6);
        }
      }
    }

    &.is-disabled {
      opacity: 0.6;
      cursor: default !important;
    }
  }
  .evidence-item {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    margin-bottom: 8px;
    padding-right: 8px;
    max-width: 100% !important;
    overflow-x: hidden !important;

    .evidence-container {
      @media (min-width: 960px) {
        display: flex;
        gap: 16px;
      }
    }

    .evidence-image {
      max-width: 100%;

      @media (min-width: 960px) {
        width: 350px;
        margin-bottom: 0;
      }

      img {
        width: 100%;
        height: auto;
        border-radius: 4px;
      }

      .loading-state {
        height: 200px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0.05);
        border-radius: 4px;
      }
    }

    .evidence-content {
      flex: 1;
    }

    .analysis-section {
      background: rgba(0, 0, 0, 0.03);
      //padding: 8px;
      border-radius: 4px;
    }
  }
  .custom-btn {
    display: block !important;
    width: 100% !important;
  }
  .v-btn--active.no-active:not(:hover)::before {
    opacity: 0 !important;
    background-color: transparent !important;
  }
  .v-btn--active::before {
    background-color: transparent !important;
  }
  .v-item-group.v-bottom-navigation .v-btn {
    min-width: 65px;
  }
  .theme--dark.v-bottom-navigation .v-btn.not-active.v-btn--active {
    color: rgba(255, 255, 255, 0.7) !important;
  }
  .theme--light.v-bottom-navigation .v-btn.not-active.v-btn--active {
    color: rgba(0, 0, 0, 0.6) !important;
  }

  .nav-bottom {
    border-top: 1px solid #ccc;
    height: auto !important;
    .clock {
      margin: 0 auto;
    }
    .btn-nav {
      padding-top: 7px !important;
      padding-bottom: constant(safe-area-inset-bottom) !important;
      padding-bottom: calc(10px + env(safe-area-inset-bottom)) !important;
    }
    .btn-nav-small {
      font-size: 9px !important;
      font-family: "roboto", sans-serif !important;
      min-width: 50px !important;
    }
    .v-btn:before {
      background-color: transparent;
    }
    @media (max-width: 449px) {
      .btn-nav-small {
        font-size: 8px !important;
      }
    }
  }

  .action-footer {
    background-color: transparent !important;
    padding-bottom: env(safe-area-inset-bottom) !important;
  }

  .action-footer::before {
    background-color: transparent !important;
  }

  /* Handle iOS safe areas */
  @supports (padding: max(0px)) {
    .action-footer {
      padding-bottom: env(safe-area-inset-bottom) !important;
    }
  }

  /* Adjust bottom spacing for iOS devices */
  @supports (-webkit-touch-callout: none) {
    .action-footer {
      bottom: calc(22px + env(safe-area-inset-bottom)) !important;
    }
  }

  .image-container {
    position: relative;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    overflow: hidden;
    background: white;
  }

  .clickable {
    cursor: pointer;
  }

  .image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .image-container:hover .image-overlay {
    opacity: 1;
  }

  .preview-image {
    background: rgba(0, 0, 0, 0.9);
  }

  .evidence-image {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.2s ease;
  }

  .image-container:hover .evidence-image {
    transform: scale(1.05);
  }

  /* Dark theme support */
  :deep(.theme-dark) .image-container {
    border-color: rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.05);
  }

  :deep(.theme-dark) .preview-image {
    background: rgba(0, 0, 0, 0.95);
  }

  .evidence-container {
    display: flex;
    gap: 16px;

    @media (max-width: 959px) {
      flex-direction: column;
    }
  }

  .evidence-details {
    flex: 1;
  }

  .comment-list {
    background: transparent;
  }

  .evidence-footer {
    background: rgba(0, 0, 0, 0.03);
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  :deep(.theme-dark) {
    .evidence-footer {
      background: rgba(255, 255, 255, 0.03);
      border-color: rgba(255, 255, 255, 0.08);
    }
  }

  .loading-placeholder {
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.05);
  }

  .image-section {
    width: 350px;
    min-width: 350px;

    @media (max-width: 959px) {
      width: 100%;
      min-width: 100%;
    }
  }

  .verification-media-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 8px;
    margin-top: 8px;

    .verification-media-item {
      border: 1px solid rgba(0, 0, 0, 0.12);
      border-radius: 4px;
      overflow: hidden;
      cursor: pointer;
      transition: transform 0.2s ease;

      &:hover {
        transform: scale(1.02);
      }
    }
  }
  .safety-controls-container {
    td {
      height: 48px !important;
    }
    .status-cell {
      .v-input {
        margin-top: -2px;
      }
    }
    .date-cell {
      min-width: 140px;
      text-align: center;
    }
  }
  .compact-table th,
  .compact-table td {
    padding: 4px 8px !important;
  }
  .verification-table {
    ::v-deep {
      tbody tr {
        td {
          padding-top: 8px !important;
          padding-bottom: 8px !important;
        }

        .v-input--selection-controls {
          margin: 0;
        }
      }

      .v-input--selection-controls__input {
        margin-bottom: 0;
      }

      /* Ensure checkbox container doesn't clip */
      .v-input--selection-controls__ripple {
        transform: translate(-50%, -50%) !important;
      }
    }
  }

  .task-status-container {
    padding: 2px 12px 10px 12px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    background-color: rgba(255, 255, 255, 0.04);
    border-radius: 4px;
    transition: background-color 0.2s ease;
    margin-top: 10px;

    :deep(.theme--dark) & {
      border-color: rgba(255, 255, 255, 0.06);
      background-color: rgba(255, 255, 255, 0.02);

      &:hover {
        background-color: rgba(255, 255, 255, 0.04);
      }
    }
  }
}

.image-preview-dialog {
  background: transparent !important;
  box-shadow: none;

  .v-card.image-preview-card {
    background: transparent !important;
    box-shadow: none;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .preview-image {
    max-height: 90vh;
    max-width: 90vw;
    object-fit: contain;
    margin: auto;
    display: block;
  }

  .v-btn.v-btn--icon {
    background: rgba(0, 0, 0, 0.4);
    color: white;

    &:hover {
      background: rgba(0, 0, 0, 0.6);
    }
  }
  &.v-dialog.image-preview-dialog {
    box-shadow: none !important;
    background: transparent !important;
  }
}
</style>
